/** Invokes the authenticated Edge Function without leaking transport shapes into React. */
import type { SupabaseClient } from '@supabase/supabase-js';

import type { Database } from '../../database.types.js';
import type { PushRemote, PushSendResult } from '../pushNotifications.js';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const readPublicKey = (value: unknown): string => {
  if (!isRecord(value) || typeof value['publicKey'] !== 'string' || value['publicKey'] === '') {
    throw new Error('The Push endpoint did not return its public key.');
  }
  return value['publicKey'];
};

const readSendResult = (value: unknown): PushSendResult => {
  if (
    !isRecord(value) ||
    typeof value['sent'] !== 'number' ||
    typeof value['expired'] !== 'number' ||
    typeof value['failed'] !== 'number'
  ) {
    throw new Error('The Push endpoint returned an invalid delivery result.');
  }
  return { sent: value['sent'], expired: value['expired'], failed: value['failed'] };
};

export const createSupabasePushRemote = (client: SupabaseClient<Database>): PushRemote => ({
  getPublicKey: async () => {
    const response = await client.functions.invoke<unknown>('push-test', { method: 'GET' });
    if (response.error) throw response.error;
    return readPublicKey(response.data);
  },
  sendTest: async () => {
    const response = await client.functions.invoke<unknown>('push-test', { method: 'POST' });
    if (response.error) throw response.error;
    return readSendResult(response.data);
  },
});
